import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel, Grid,
  TextField,
  Theme,
  Typography
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import moment from 'moment';
import React, {useState} from 'react';
import {FilterValues} from './LogList';
import {WcvType, WcvTypes} from '../../../graphql/logs.datatypes';

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  content: {
    padding: theme.spacing(0, 3),
    flexGrow: 1
  },
  contentSection: {
    padding: theme.spacing(2, 0)
  },
  contentSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  contentSectionContent: {},
  formGroup: {
    padding: theme.spacing(2, 0)
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center'
  },
  field: {
    marginTop: 0,
    marginBottom: 0
  },
  flexGrow: {
    flexGrow: 1
  },
  addButton: {
    marginLeft: theme.spacing(1)
  },
  tags: {
    marginTop: theme.spacing(1)
  },
  radioGroup: {},
  buttonIcon: {
    marginRight: theme.spacing(1)
  },
  actions: {
    padding: theme.spacing(3),
    '& > * + *': {
      marginTop: theme.spacing(2)
    }
  }
}));

export const SearchFilter = (props: { applyFilters: (filters: FilterValues) => void, filters: FilterValues, initialFilters: FilterValues, notes: boolean, hideType: boolean }) => {
  const {filters, initialFilters} = props;
  const classes = useStyles();
  const [values, setValues] = useState({...filters});

  const handleClear = () => {
    props.applyFilters(initialFilters);
  };

  const handleFieldChange = (event: any, field: string, value: string | boolean) => {
    if (event.persist) event.persist();

    setValues(oldValues => ({
      ...oldValues,
      [field]: event.target.type === 'checkbox' ? event.target.checked : value
    }));
  };

  const handleTagAdd = () => {
    setValues(oldValues => {
      const newValues = { ...oldValues };

      if (newValues.tag && !newValues.tags.includes(newValues.tag)) {
        newValues.tags = [...newValues.tags];
        newValues.tags.push(newValues.tag);
      }

      newValues.tag = '';

      return newValues;
    });
  };

  const handleTagDelete = (tag: string) => {
    setValues(oldValues => {
      const newValues = { ...oldValues };

      newValues.tags = newValues.tags.filter(t => t !== tag);

      return newValues;
    });
  };

  const applyFilters = () => {
    props.applyFilters(values);
  };

  return (
    <React.Fragment>
      <div className={classes.content}>
        <div className={classes.contentSection}>

          <div className={classes.formGroup}>
            <Typography component="p" gutterBottom variant="overline">
              Date Range
            </Typography>
            <div className={classes.fieldGroup} style={{paddingBottom: 10}}>
              <TextField
                className={classes.field}
                defaultValue={moment(values.startDate).format('YYYY-MM-DDTHH:mm:ss')}
                fullWidth
                label="Start date"
                name="startDate"
                onChange={(event: any) =>
                  handleFieldChange(
                    event,
                    'startDate',
                    event.target.value
                  )
                }
                type="datetime-local"
                variant="outlined"
              />
            </div>
            <div className={classes.fieldGroup}>
              <TextField
                className={classes.field}
                defaultValue={moment(values.endDate).format('YYYY-MM-DDTHH:mm:ss')}
                fullWidth
                label="End date"
                name="endDate"
                onChange={(event: any) =>
                  handleFieldChange(
                    event,
                    'endDate',
                    event.target.value
                  )
                }
                type="datetime-local"
                variant="outlined"
              />
            </div>
          </div>

          {!props.hideType && (
              <div className={classes.formGroup}>
                <TextField
                    fullWidth
                    label="Type"
                    name="type"
                    onChange={(event: any) =>
                        handleFieldChange(
                            event,
                            'type',
                            event.target.value
                        )
                    }
                    select
                    // eslint-disable-next-line react/jsx-sort-props
                    SelectProps={{native: true}}
                    value={values.type}
                    variant="outlined"
                    InputLabelProps={{shrink: true}}
                >
                  <option value={-1}>All Types</option>
                  {WcvTypes.map((type, index) => (
                      <option key={index} value={index}>
                        {type.name}
                      </option>
                  ))}
                </TextField>
              </div>
          )}


          {!props.notes && (
            <FormControl component="fieldset" className={classes.formGroup}>
              <FormLabel component="legend">Log Severity</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={values.levelDebug}
                                     onChange={event => handleFieldChange(event, 'levelDebug', event.target.checked)}
                                     value="levelDebug"/>}
                  label="Debug"
                />
                <FormControlLabel
                  control={<Checkbox checked={values.levelInfo}
                                     onChange={event => handleFieldChange(event, 'levelInfo', event.target.checked)}
                                     value="levelInfo"/>}
                  label="Info"
                />
                <FormControlLabel
                  control={<Checkbox checked={values.levelWarn}
                                     onChange={event => handleFieldChange(event, 'levelWarn', event.target.checked)}
                                     value="levelWarn"/>}
                  label="Warn"
                />
                <FormControlLabel
                  control={<Checkbox checked={values.levelError}
                                     onChange={event => handleFieldChange(event, 'levelError', event.target.checked)}
                                     value="levelError"/>}
                  label="Error"
                />
                <FormControlLabel
                  control={<Checkbox checked={values.levelFatal}
                                     onChange={event => handleFieldChange(event, 'levelFatal', event.target.checked)}
                                     value="levelFatal"/>}
                  label="Fatal"
                />
              </FormGroup>
            </FormControl>
          )}

          <div className={classes.formGroup}>
            <Typography component="p" gutterBottom variant="overline">
              Log Codes
            </Typography>
            <div className={classes.fieldGroup}>
              <TextField
                className={clsx(classes.field, classes.flexGrow)}
                label="Log code"
                margin="dense"
                name="tag"
                onChange={event =>
                  handleFieldChange(event, 'tag', event.target.value)
                }
                value={values.tag}
                variant="outlined"
                onKeyPress={ e => { if(e.key === 'Enter') handleTagAdd(); }}
              />
              <Button
                className={classes.addButton}
                onClick={handleTagAdd}
                size="small"
              >
                <AddIcon />
                Add
              </Button>
            </div>
            <div className={classes.tags}>
              {values.tags.map(tag => (
                <Chip
                  deleteIcon={<CloseIcon />}
                  key={tag}
                  label={tag}
                  onDelete={() => handleTagDelete(tag)}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
      <div className={classes.actions}>
        <Button id="clear" fullWidth onClick={handleClear} variant="contained">
          <DeleteIcon className={classes.buttonIcon}/>
          Clear
        </Button>
        <Button id="submit" color="primary" fullWidth variant="contained" onClick={applyFilters}>
          Apply filters
        </Button>
      </div>

    </React.Fragment>
    );
};